import { Component } from "react";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import AddAdvertiserDialog from "./components/AddAdvertiserDialog/AddAdvertiserDialog";
import { formatLocalDateTime } from "utils/dateFormatter";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import AdvertiserCard from "../MediInStepAdvertiserScreen/components/AdvertiserCard/AdvertiserCard";
import { useMaterialUIController } from "context";

interface AdvertiserListState {
    advertisers: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    addAdvertiserDialogOpen: boolean;
    editingAdvertiser: any | null;
    isDeleteDialogOpen: boolean;
    deletingAdvertiser: any | null;
    deleteError: string | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_advertiser_list_page_size";

class AdvertiserListScreenClass extends Component<any, AdvertiserListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            advertisers: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            addAdvertiserDialogOpen: false,
            editingAdvertiser: null,
            isDeleteDialogOpen: false,
            deletingAdvertiser: null,
            deleteError: null,
        };
    }

    async componentDidMount() {
        this.loadAdvertisers();
    }



    loadAdvertisers = async () => {
        try {
            this.setState({ loading: true });

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchTerm || undefined,
                TenantId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
            };

            const res = await request("Advertiser.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const advertisers = (pagedList.items || []).map((advertiser: any) => ({
                    seqId: advertiser.seqId,
                    name: advertiser.name || "-",
                    managerName: advertiser.managerName || "-",
                    managerContact: advertiser.managerContact || "-",
                    contractStartDate: advertiser.contractStartDate
                        ? new Date(advertiser.contractStartDate).toLocaleDateString()
                        : "-",
                    contractEndDate: advertiser.contractEndDate
                        ? new Date(advertiser.contractEndDate).toLocaleDateString()
                        : "-",
                    status: advertiser.status || (advertiser.isActive ? "ACTIVE" : "INACTIVE"),
                    created: advertiser.crtDt ? formatLocalDateTime(advertiser.crtDt) : "-",
                    rawCrtDt: advertiser.crtDt ? (advertiser.crtDt.endsWith("Z") ? advertiser.crtDt : `${advertiser.crtDt}Z`) : null,
                }));

                this.setState({
                    advertisers,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("광고주 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleAdvertiserClick = (advertiser: any) => {
        console.log("광고주 클릭:", advertiser);
        // TODO: 광고주 상세 페이지로 이동 또는 다이얼로그 열기
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            this.loadAdvertisers();
        });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadAdvertisers();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadAdvertisers();
        });
    };

    handleAddAdvertiserOpen = () => {
        this.setState({ addAdvertiserDialogOpen: true });
    };

    handleAddAdvertiserClose = () => {
        this.setState({ addAdvertiserDialogOpen: false, editingAdvertiser: null });
    };

    handleAddAdvertiserSuccess = () => {
        this.setState({ pageNumber: 1, searchTerm: "", editingAdvertiser: null }, () => {
            this.loadAdvertisers();
        });
    };

    handleEdit = (advertiser: any) => {
        this.setState({
            editingAdvertiser: advertiser,
            addAdvertiserDialogOpen: true,
        });
    };

    handleOpenDeleteDialog = (advertiser: any) => {
        this.setState({ deletingAdvertiser: advertiser, isDeleteDialogOpen: true });
    };

    handleCloseDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false, deletingAdvertiser: null, deleteError: null });
    };

    handleConfirmDelete = async () => {
        if (!this.state.deletingAdvertiser) return;

        try {
            this.setState({ loading: true, deleteError: null });
            console.log('Deleting advertiser:', this.state.deletingAdvertiser);
            console.log('Delete request params:', {
                pathParams: { id: this.state.deletingAdvertiser.seqId }
            });
            await request('Advertiser.delete', {
                pathParams: { id: this.state.deletingAdvertiser.seqId }
            });
            this.setState({ loading: false, isDeleteDialogOpen: false, deletingAdvertiser: null });
            this.loadAdvertisers();
        } catch (error: any) {
            console.error('광고주 삭제 실패:', error);
            console.error('Error response:', error.response);
            console.error('Error response data:', error.response?.data);
            console.error('Error response status:', error.response?.status);
            console.error('Error config:', error.config);
            this.setState({ loading: false, deleteError: error?.response?.data?.message || '광고주 삭제 중 오류가 발생했습니다.' });
        }
    };

    render() {
        const { advertisers, loading, searchTerm, pageSize, pageNumber, totalCount, addAdvertiserDialogOpen } = this.state;

        console.log('AdvertiserListScreen - miniSidenav:', this.props.miniSidenav);

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 - Card 배경 유지 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">campaign</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        광고주 목록 ( ADVERTISER LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    onClick={this.handleAddAdvertiserOpen}
                                >
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    광고주 추가
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: '#9e9e9e' }}>
                                                        search
                                                    </Icon>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 - 배경 없음 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        ) : advertisers.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 광고주가 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {advertisers.map((advertiser: any) => {
                                        // 사이드바 상태에 따라 다른 Grid 설정
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 } // 사이드바 닫힘: ~1200px: 3개, 1536px~: 5개
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }; // 사이드바 열림: 모든 화면에서 3개 유지

                                        // 1920px 이상에서 추가 설정
                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%', // 6개 (12/6 = 2)
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%', // 5개 (12/5 = 2.4)
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={advertiser.seqId}>
                                                <AdvertiserCard
                                                    advertiser={advertiser}
                                                    onClick={this.handleAdvertiserClick}
                                                    onEdit={this.handleEdit}
                                                    onDelete={() => this.handleOpenDeleteDialog(advertiser)}
                                                    darkMode={this.props.darkMode}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* 페이지네이션 */}
                                {this.state.totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center">
                                        <Pagination
                                            count={Math.ceil(this.state.totalCount / this.state.pageSize)}
                                            page={this.state.pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddAdvertiserDialog
                    open={this.state.addAdvertiserDialogOpen}
                    editingAdvertiser={this.state.editingAdvertiser}
                    onClose={this.handleAddAdvertiserClose}
                    onSuccess={this.handleAddAdvertiserSuccess}
                    theme={this.props.theme}
                    darkMode={this.props.darkMode}
                />

                {/* 삭제 확인 다이얼로그 */}
                <Dialog
                    open={this.state.isDeleteDialogOpen}
                    onClose={this.handleCloseDeleteDialog}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? '#1a2035' : '#fff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: this.props.darkMode ? '#fff' : '#000' }}>
                        광고주 삭제 확인
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                            "{this.state.deletingAdvertiser?.name}" 광고주를 삭제하시겠습니까?<br />
                            이 작업은 취소할 수 없습니다.
                        </DialogContentText>
                        {this.state.deleteError && (
                            <MDBox mt={2}>
                                <p style={{ color: '#f44336', fontSize: '0.875rem', margin: 0 }}>
                                    {this.state.deleteError}
                                </p>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleCloseDeleteDialog} color="secondary">
                            취소
                        </MDButton>
                        <MDButton
                            onClick={this.handleConfirmDelete}
                            variant="gradient"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? '삭제 중...' : '삭제'}
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

function AdvertiserListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <AdvertiserListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default AdvertiserListScreen;
